import JobApplicationMeta from './JobApplicationMeta'
import JobApplicationOffer from './JobApplicationOffer'
import cloneDeep from 'lodash/cloneDeep'
import maxBy from 'lodash/maxBy'
import unescape from 'lodash/unescape'
import { FileCategories } from '@/constants/backend/FileCategories'
import { Blockchain } from '@/constants/blockchain'
import { CURRENCY_FIELD_BACKEND_ID } from '@/constants/currency'
import { JobApplicationStatuses } from '@/constants/job/jobApplicationStatuses'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import { JobStage, STAGE_COMPLETED, STAGE_DEADLINE_OVERDUE, STAGE_DISPUTED, STAGE_NEW, STAGE_STARTED } from '@/constants/job/jobStages'
import { JobStatus } from '@/constants/job/jobStatuses'
import UserInfo from '@/models-ts/user/UserInfo'
import { getCurrency } from '@/utils-ts/currencies'
import { parseSlug } from '@/utils-ts/parser'
import { addDays, convertToLocal, getDateFromString } from '@/utils/date'
import { getUrl } from '@/utils/file'
import { formatCurrency, formatUsd } from '@/utils/moneyFormat'

export default class JobApplication {
  id: number
  status: JobApplicationStatuses
  comment: string
  offer: JobApplicationOffer | null
  deadline: number
  budget: string
  isRead: boolean
  createdAt: string
  updatedAt: string
  selectedFreelancerId: number | null
  job: {
    id: number
    slug: string
    status: JobStatus
    stage: JobStage
    name: string
    budget: string
    deliveryTimeAt?: string | null
    blockchain: Blockchain
    currency: number | null
    escrowBalance?: string | null
    escrowBalanceDecimals?: string | null
    escrowBalanceUsd?: string | null
    estimate: number | null
    inProgressAt: string | null
    isDone: boolean
    contractVersion: number | null
    invoiceUrl: string | null
    txIdCreated?: string | null
    txIdCompleted?: string | null
    completedAt?: string | null
    invoiceDocumentUrl?: string
  }
  freelancer: UserInfo | null
  customer: UserInfo | null
  hasOffers: boolean
  meta?: JobApplicationMeta | null
  freelancerLeftReview: boolean
  customerLeftReview: boolean

  constructor(data: Partial<JobApplication>) {
    Object.assign(this, cloneDeep(data))
  }

  get currencyModel() {
    return getCurrency({
      blockchain: this.job.blockchain,
      value: this.job?.currency,
      field: CURRENCY_FIELD_BACKEND_ID,
    })
  }

  get currencyTokenName() {
    return this.currencyModel ? this.currencyModel.name : 'N/A'
  }

  get offerBudgetUsdFormatted() {
    if (this.offer?.budget) {
      return formatUsd(this.offer?.budget)
    }
    return 'N/A'
  }

  get budgetFormatted() {
    if (this.job?.escrowBalanceDecimals) {
      return formatCurrency(this.job?.escrowBalanceDecimals, { currency: this.currencyModel })
    }
    return 'N/A'
  }

  get budgetUsdFormatted() {
    if (this.job?.escrowBalanceUsd) {
      return formatUsd(this.job?.escrowBalanceUsd)
    }
    return 'N/A'
  }

  static fromServer(data: LX.JobApplication) {
    const offer = maxBy(
      (data.relations?.Offers || []).map((offer) => ({
        ...offer,
        createdDate: +getDateFromString(offer.created_at),
      })),
      'createdDate'
    )

    const invoiceFile = (data.relations.Job.relations?.File || []).find((file: any) => file.category === FileCategories.CAT_GENERATED_PDF)
    const invoiceUrl = invoiceFile ? getUrl(invoiceFile) : null
    const job = data.relations?.Job
    const reviews = job.relations?.Review || []
    const freelancer = data.relations?.Freelancer
    const customer = data.relations?.Customer

    return new JobApplication({
      id: data.id,
      status: data.status,
      isRead: Boolean(data.is_read),
      comment: data.comment || '',
      deadline: data.deadline,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      budget: data?.budget?.amount,
      selectedFreelancerId: data.relations?.Job?.freelancer_id || data.relations?.Freelancer?.id,
      offer: offer ? JobApplicationOffer.fromServer(offer) : null,
      hasOffers: !!(data.relations?.Offers || []).length,
      job: {
        id: job.id || 0,
        slug: parseSlug(job?.slug),
        stage: job.stage,
        status: job.status,
        name: unescape(job.name),
        deliveryTimeAt: job.delivery_time_at,
        budget: job.budget ? String(Number(job.budget).toFixed(2)) : '0.00',
        blockchain: job.blockchain,
        currency: job.currency,
        escrowBalance: job?.escrow_balance,
        escrowBalanceDecimals: job.escrow_balance_decimal,
        escrowBalanceUsd: job.escrow_balance_usd,
        estimate: data.deadline ? data.deadline / 86400 : 0, // from seconds to day
        inProgressAt: job.in_progress_at ? convertToLocal(job.in_progress_at) : null,
        isDone: Boolean(job.is_done || false),
        contractVersion: job.contract_version,
        invoiceUrl,
        txIdCreated: job.txid_created,
        txIdCompleted: job.txid_completed,
        completedAt: job.completed_at,
      },
      customer: customer ? UserInfo.fromServer(customer) : null,
      freelancer: freelancer ? UserInfo.fromServer(freelancer) : null,
      meta: data.tabs_meta,
      freelancerLeftReview: reviews.some((item) => item.from_user_id === freelancer?.id),
      customerLeftReview: reviews.some((item) => item.from_user_id === customer?.id),
    })
  }

  get estimatedDeadline() {
    if (this.wasStarted) {
      const inProgressAt = this.job.inProgressAt || ''
      const estimate = this.job.estimate || 0
      return addDays(inProgressAt, estimate)
    }
  }

  get deadlineInDays() {
    return (this.deadline || 0) / 86400
  }

  get wasStarted() {
    return ![STAGE_NEW, STAGE_STARTED].includes(this.job.stage)
  }

  get isCompleted() {
    return (
      [STAGE_COMPLETED, STAGE_DEADLINE_OVERDUE, STAGE_DISPUTED].includes(this.job.stage) &&
      this.offer?.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER
    )
  }

  get sortField() {
    return this.updatedAt || this.createdAt
  }
}
